import { useState } from 'react'
import moment from 'moment'
import { Carousel } from 'nuka-carousel'
import ProductContactModal from '../../../components/ivystore/product/ProductContactModal'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
import priceFormatter from '../../../lib/priceFormatter'
import StoreBlockCatalog from '../../../components/ivystore/store/StoreBlockCatalog'
import { fetchSEO } from '../../../lib/blog/api'
import Seo from '../../../components/blog/seo'
import baseUrl from '../../../lib/base-url'
import BreadCrumbs from '../../../components/ivystore/BreadCrumbs'
import Link from 'next/link'
import { useUser } from 'next-firebase-auth'
import populateSeoWith from '../../../lib/populate-seo'
import StoreBlockContacts from '../../../components/ivystore/store/StoreBlockContacts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil } from '@fortawesome/free-solid-svg-icons'

const imageHeight = 500
const ImagesGallery = ({ images, title, showFullscreenGallery }) => {
  if (!images?.length) {
    images = [{ url: '/img/placeholder.png' }]
  }

  // image rows: odd rows have 1 image, even rows have 2 images
  const imageRows = []
  let i = 0
  while (i < images.length) {
    if (i % 2 === 0) {
      // Odd row (0-based index): 1 image
      imageRows.push([images[i]])
      i += 1
    } else {
      // Even row (0-based index): 2 images
      imageRows.push(images.slice(i, i + 2))
      i += 2
    }
  }

  return (
    <div
      className="d-flex flex-column gap-1 w-100"
    >
      {imageRows.map((imagesArr, index) => (
        <div
          key={index}
          className={'d-flex gap-1'}
          style={{ height: imagesArr.length === 1 ? imageHeight : imageHeight / 2 }}
        >
          {imagesArr.map((image) => (
            <div
              key={image.url}
              className={'w-100 flex-shrink-0 bg-light br-2'}
              style={{ flexBasis: imagesArr.length === 1 ? '100%' : '49.8%' }}
            >
              <img
                src={image.url}
                alt={`${title} image | Ivy Store`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'zoom-in',
                }}
                className={'br-2'}
                onClick={() => {
                  showFullscreenGallery(title, images)
                }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function CouchProperty ({ label, value }) {
  if (!value) {
    return null
  }

  return (
    <div className={'d-flex gap-2'}>
      <div className={'text-muted'}>
        {label}
      </div>
      <div className={'text-capitalize'}>
        {value}
      </div>
    </div>
  )
}

const FullScreenGallery = ({ fullscreenGallery, setFullscreenGallery }) => {
  return (
    <Modal
      size="xl"
      fullscreen
      centered
      show
      onHide={() => setFullscreenGallery({
        visible: false,
        title: '',
        images: [],
      })}
      className="fullscreen-gallery"
    >
      <Modal.Header closeButton>
        {fullscreenGallery.title}
      </Modal.Header>
      <Carousel
        className="bg-transparent br-2"
        style={{
          width: '100%',
          height: '90vh',
        }}
        showArrows
        wrapMode={'wrap'}
      >
        {fullscreenGallery.images.map((image, index) => (
          <div
            key={index}
            className={'w-100 flex-shrink-0'}
            style={{ flexBasis: '100%' }}
          >
            <img
              src={image.url}
              alt={`Image fullscreen | Ivy Store`}
              style={{
                width: '100%',
                height: '90vh',
                objectFit: 'contain',
              }}
            />
          </div>
        ))}
      </Carousel>
    </Modal>
  )
}

function shuffleArray (array) {
  let currentIndex = array.length, temporaryValue, randomIndex

  // While there remain elements to shuffle...
  while (0 !== currentIndex) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex -= 1

    // And swap it with the current element.
    temporaryValue = array[currentIndex]
    array[currentIndex] = array[randomIndex]
    array[randomIndex] = temporaryValue
  }

  return array
}

export const getServerSideProps = async ({ query }) => {
  const { slug, productSlug } = query

  let storeData = null
  let stateData = null
  let item = null
  let relatedItems = []

  try {
    const storeResponse = await fetch(`${baseUrl}/api/ivy-store/store?slug=${slug}`)
    const resData = await storeResponse.json()

    if (resData.success) {
      storeData = resData.data
    } else {
      console.error('Error fetching store data:', resData)
    }
  } catch (error) {
    console.error('Error fetching store data:', error)
  }

  if (storeData) {
    try {
      const geographyResponse = await fetch(`${baseUrl}/api/ivy-store/geography?stateSlug=${storeData.location.state}`)
      const { data } = await geographyResponse.json()
      stateData = data
    } catch (error) {
      console.error('Failed to fetch states data:', error.message)
    }

    try {
      // Fetch all store items at once
      const itemsResponse = await fetch(`${baseUrl}/api/ivy-store/store-items?userId=${storeData.id}`)
      const itemsData = await itemsResponse.json()

      if (itemsData.success) {
        // Find the target item
        item = itemsData.data.find(i => i.slug === productSlug) ?? null

        if (item) {
          // Filter and shuffle related items
          const filtered = itemsData.data.filter(i => i.slug !== productSlug)
          relatedItems = shuffleArray(filtered).slice(0, 4)
        }
      } else {
        console.error('Error fetching item data:', itemsData)
      }
    } catch (error) {
      console.error('Error fetching item data:', error)
    }
  }

  if (!item) {
    return {
      notFound: true
    }
  }

  const seo = await fetchSEO('ivystore-store-product')
  populateSeoWith(seo, {
    product_name: item.data.title,
    store_name: storeData?.name,
    price: item.data.price,
    condition: item.data.condition,
  })

  return {
    props: {
      seo,
      stateData,
      storeData,
      item,
      relatedItems,
    },
  }
}

export default function ProductPage ({ seo, stateData, storeData, item, relatedItems }) {
  const authUser = useUser()

  const [showContactModal, setShowContactModal] = useState(false)
  const [fullscreenGallery, setFullscreenGallery] = useState({
    visible: false,
    title: '',
    images: [],
  })

  function showFullscreenGallery (title, images) {
    setFullscreenGallery({
      visible: true,
      title,
      images,
    })
  }

  const cityData = stateData?.cities.find(city => city.slug === storeData.location.city)

  const isMyStore = storeData.id === authUser.id

  return (
    <>
      <Seo seo={seo} />

      <div className={'d-flex justify-content-between'}>
        <BreadCrumbs
          stateData={stateData}
          cityData={cityData}
          storeData={storeData}
        />

        {isMyStore && (
          <div className={'flex-shrink-0'}>
            <Link
              href={`/app/store`}
              className={'btn btn-sm btn-light'}
            >
              <FontAwesomeIcon
                icon={faPencil}
                width={16}
                height={16}
                className={'me-1'}
              />
              Edit Store
            </Link>
          </div>
        )}
      </div>

      <div
        className={'d-flex flex-column'}
        style={{ gap: '4rem' }}
      >
        <Row>
          <Col
            xs={12} lg={7}
            className={'pe-lg-4 pe-2'}
          >
            <div>
              <ImagesGallery
                title={item.data.title}
                images={item.data.images}
                showFullscreenGallery={showFullscreenGallery}
              />
            </div>
          </Col>
          <Col xs={12} lg={5}>
            <div
              className={'d-flex flex-column gap-4 sticky-top pt-2'}
            >
              <div className={'mt-4 mt-lg-0'}>
                <h1 className={'m-0 fs-2'}>
                  {item.data.title}
                </h1>
              </div>

              <div className={'fs-4'}>
                {`${priceFormatter.format(item.data.price)} USD`}
              </div>

              <div>
                <Button
                  variant={'success'}
                  size={'lg'}
                  onClick={() => setShowContactModal(true)}
                >
                  Contact seller
                </Button>
              </div>

              <div className={'d-flex flex-column gap-2'}>
                <CouchProperty label={'Condition'} value={item.data.condition} />
                <CouchProperty label={'Type'} value={item.data.sofaType} />
                <CouchProperty label={'Brand'} value={item.data.brand} />
                <CouchProperty label={'Color'} value={item.data.color} />
                <CouchProperty label={'Decor Style'} value={item.data.decorStyle} />
                <CouchProperty
                  label={'Located at'}
                  value={[item.store_data?.location?.state, item.store_data?.location?.city].filter(Boolean).join(', ')}
                />
              </div>

              {item.data.description && (
                <div>
                  <h2 className={'fs-6 mb-0 text-muted'}>
                    Description
                  </h2>
                  <div
                    className={'lh-lg'}
                    dangerouslySetInnerHTML={{ __html: item.data.description?.replace(/\n/g, '<br />') }}
                  />
                </div>
              )}

              {item.data.created_at && (
                <div className="text-muted small">
                  {moment(item.data.created_at.seconds * 1000).format('MMM DD HH:mm')}
                </div>
              )}
            </div>
          </Col>
        </Row>

        {relatedItems.length > 0 && (
          <div>
            <StoreBlockCatalog
              items={relatedItems}
              storeData={storeData}
              label={'More from this store'}
            />
          </div>
        )}

        <StoreBlockContacts storeData={storeData} />
      </div>

      <ProductContactModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        itemId={item.id}
        storeId={item.user_id}
      />

      {fullscreenGallery.visible && (
        <FullScreenGallery
          setFullscreenGallery={setFullscreenGallery}
          fullscreenGallery={fullscreenGallery}
        />
      )}
    </>
  )
}
